<template>
  <section ref="section" class="lazy-section" :data-section="name">
    <slot v-if="ready"></slot>
    <div v-else class="section-placeholder">
      <strong>{{ placeholder }}</strong>
    </div>
  </section>
</template>

<script>
// Pixels below the viewport at which a section counts as "about to be seen".
const PRELOAD_MARGIN = 180;

/**
 * Shows a placeholder until `ready`, and emits `visible` once the section scrolls near the
 * viewport (after `active` turns on), so the parent can load that section's data on demand.
 */
export default {
  name: "LazySection",
  props: {
    name: { type: String, required: true },
    ready: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
  },
  emits: ["visible"],
  data() {
    return { observer: null, scrollHandler: null };
  },
  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) this.$nextTick(this.startWatching);
      },
    },
  },
  beforeUnmount() {
    this.stopWatching();
  },
  methods: {
    startWatching() {
      const element = this.$refs.section;
      if (!element || this.observer || this.scrollHandler) return;

      if (!("IntersectionObserver" in window)) {
        // Older browsers: check position on scroll until the section has loaded.
        this.scrollHandler = () => {
          if (!this.ready && element.getBoundingClientRect().top <= window.innerHeight + PRELOAD_MARGIN) {
            this.$emit("visible", this.name);
          }
        };
        this.scrollHandler();
        window.addEventListener("scroll", this.scrollHandler, { passive: true });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.$emit("visible", this.name);
            this.stopWatching();
          }
        },
        { root: null, rootMargin: `${PRELOAD_MARGIN}px 0px`, threshold: 0.05 },
      );
      this.observer.observe(element);
    },
    stopWatching() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }
    },
  },
};
</script>
